import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader2, GraduationCap, CheckCircle, XCircle, Mail, Globe, Phone, Users, Building2, FolderKanban, FlaskConical, Clock, Wallet, Eye, ToggleRight } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const fmtINR = (paise) => `₹${((paise || 0) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function AdminUniversities({ token, API }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/admin/universities`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setRows((await res.json()).universities || []);
    setLoading(false);
  }, [API, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const act = async (id, action) => {
    const res = await fetch(`${API}/admin/universities/${id}/${action}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) { toast.success(action === 'approve' ? 'Approved' : 'Rejected'); refresh(); }
    else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
  };

  const openOverview = async (uni) => {
    setOverview({ loading: true, university: uni });
    setLoadingOverview(true);
    try {
      const res = await fetch(`${API}/admin/universities/${uni.id}/overview`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setOverview(await res.json());
      else { toast.error('Failed to load overview'); setOverview(null); }
    } finally { setLoadingOverview(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!rows.length) return <p className="text-sm text-zinc-500 py-6 text-center">No university registrations yet.</p>;
  return (
    <>
    <div className="space-y-3">
      {rows.map(u => (
        <Card key={u.id} className="bg-zinc-800 border-zinc-700" data-testid={`admin-uni-${u.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1 cursor-pointer" onClick={() => u.is_approved && openOverview(u)}>
                <h4 className="font-semibold text-white flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-primary" /> {u.name}
                  {u.is_approved && <Eye className="w-3 h-3 text-zinc-400" />}
                </h4>
                <div className="text-xs text-zinc-400 mt-1 space-y-0.5">
                  <p>{u.admin_name} · <Mail className="w-3 h-3 inline" /> {u.admin_email}</p>
                  {u.contact_phone && <p><Phone className="w-3 h-3 inline" /> {u.contact_phone}</p>}
                  {u.website && <p><Globe className="w-3 h-3 inline" /> <a className="text-primary hover:underline" href={u.website} target="_blank" rel="noreferrer">{u.website}</a></p>}
                  <p className="text-[10px]">Registered {new Date(u.created_at).toLocaleString()}</p>
                </div>
              </div>
              <div className="flex flex-col gap-2 items-end">
                {u.is_approved ? (
                  <Badge className="bg-emerald-500/10 text-emerald-400">Approved</Badge>
                ) : u.rejected_at ? (
                  <Badge className="bg-zinc-500/20 text-zinc-300">Rejected</Badge>
                ) : (
                  <Badge className="bg-amber-500/10 text-amber-400">Pending</Badge>
                )}
                {u.is_approved && (
                  <Button size="sm" variant="outline" onClick={() => openOverview(u)} className="gap-1 border-zinc-600 text-zinc-200 hover:bg-zinc-700" data-testid={`uni-view-${u.id}`}>
                    <Eye className="w-3 h-3" /> View
                  </Button>
                )}
              </div>
            </div>
            {!u.is_approved && (
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={() => act(u.id, 'approve')} className="gap-1" data-testid={`uni-approve-${u.id}`}>
                  <CheckCircle className="w-4 h-4" /> Approve
                </Button>
                <Button size="sm" variant="outline" onClick={() => act(u.id, 'reject')} className="gap-1 border-zinc-600 text-zinc-200 hover:bg-zinc-700" data-testid={`uni-reject-${u.id}`}>
                  <XCircle className="w-4 h-4" /> Reject
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>

    <Dialog open={!!overview} onOpenChange={(v) => !v && setOverview(null)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-700 text-zinc-100">
        {overview && (
          <>
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary" /> {overview.university?.name}
              </DialogTitle>
              <DialogDescription className="text-zinc-400">University overview — aggregated metrics across its network</DialogDescription>
            </DialogHeader>
            {loadingOverview || overview.loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <OvCard icon={Users} label="Experts" value={overview.counts?.experts} />
                  <OvCard icon={Building2} label="Industry" value={overview.counts?.industry} />
                  <OvCard icon={FolderKanban} label="Projects" value={overview.counts?.projects} />
                  <OvCard icon={FolderKanban} label="Active Projects" value={overview.counts?.active_projects} />
                  <OvCard icon={FlaskConical} label="Labs" value={overview.counts?.labs} />
                  <OvCard icon={Clock} label="Pending Bookings" value={overview.counts?.bookings_pending} />
                  <OvCard icon={CheckCircle} label="Approved Bookings" value={overview.counts?.bookings_approved} />
                  <OvCard icon={CheckCircle} label="Paid Bookings" value={overview.counts?.bookings_paid} />
                </div>
                <ModuleToggles API={API} token={token} uni={overview.university} onSaved={(modules) => { setOverview(o => ({ ...o, university: { ...o.university, modules } })); refresh(); }} />
                <Card className="bg-zinc-800 border-zinc-700">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-white"><Wallet className="w-4 h-4 text-primary" /> Financials</h4>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div><p className="text-[10px] uppercase text-zinc-400">Projects Gross</p><p className="font-semibold mt-1">{fmtINR(overview.financials_paise?.project_gross)}</p></div>
                      <div><p className="text-[10px] uppercase text-zinc-400">Labs Gross</p><p className="font-semibold mt-1">{fmtINR(overview.financials_paise?.lab_gross)}</p></div>
                      <div><p className="text-[10px] uppercase text-zinc-400">Total Gross</p><p className="font-semibold mt-1 text-primary">{fmtINR(overview.financials_paise?.total_gross)}</p></div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}

function OvCard({ icon: Icon, label, value }) {
  return (
    <Card className="bg-zinc-800 border-zinc-700">
      <CardContent className="p-3">
        <p className="text-[10px] uppercase text-zinc-400 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</p>
        <p className="text-lg font-bold mt-1 text-white">{value ?? 0}</p>
      </CardContent>
    </Card>
  );
}

const MODULES = [
  { key: 'projects', label: 'Projects', hint: 'Affiliated innovators can view and work on industry projects' },
  { key: 'opportunities', label: 'Opportunities', hint: 'Affiliated innovators can submit to opportunities' },
  { key: 'labs', label: 'Labs', hint: 'Industry can book this university\'s labs' },
];

function ModuleToggles({ API, token, uni, onSaved }) {
  const [saving, setSaving] = useState(null);
  const modules = uni?.modules || { projects: true, opportunities: true, labs: true };
  const toggle = async (key, value) => {
    setSaving(key);
    try {
      const res = await fetch(`${API}/admin/universities/${uni.id}/modules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [key]: value }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { toast.success(`${key[0].toUpperCase() + key.slice(1)} module ${value ? 'enabled' : 'disabled'}`); onSaved(d.modules); }
      else toast.error(d.detail || 'Failed');
    } finally { setSaving(null); }
  };
  return (
    <Card className="bg-zinc-800 border-zinc-700" data-testid="admin-uni-modules">
      <CardContent className="p-4">
        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-white"><ToggleRight className="w-4 h-4 text-primary" /> Modules</h4>
        <div className="space-y-3">
          {MODULES.map(m => (
            <div key={m.key} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-white">{m.label}</p>
                <p className="text-[11px] text-zinc-400">{m.hint}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {saving === m.key && <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />}
                <span className={`text-[10px] uppercase ${modules[m.key] ? 'text-emerald-400' : 'text-amber-400'}`}>{modules[m.key] ? 'On' : 'Off'}</span>
                <Switch checked={!!modules[m.key]} disabled={!!saving} onCheckedChange={(v) => toggle(m.key, v)} data-testid={`module-toggle-${m.key}`} />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
